export const CANONICAL_SERVICES = [
  'Sensitivity Reading', 'Beta Reading', 'Alpha Reading',
  'Book Cover Design',   'Author PA',    'Editing',
  'Formatting',          'PR / Marketing', 'Social Media Manager',
  'Ghostwriting',
];

// Alternate spellings/phrasings that should count as the canonical name.
export const SERVICE_ALIASES = {
  'ghostwriter': 'Ghostwriting', 'ghost writer': 'Ghostwriting', 'ghost writing': 'Ghostwriting',
  'copyediting': 'Editing', 'copy editing': 'Editing', 'copy editor': 'Editing',
  'proofreading': 'Editing', 'proofreader': 'Editing',
  'line editing': 'Editing', 'line editor': 'Editing',
  'developmental editing': 'Editing', 'developmental editor': 'Editing', 'dev editing': 'Editing',
  'substantive editing': 'Editing',
  'beta reader': 'Beta Reading', 'beta read': 'Beta Reading',
  'alpha reader': 'Alpha Reading', 'alpha read': 'Alpha Reading',
  'sensitivity reader': 'Sensitivity Reading', 'sensitivity read': 'Sensitivity Reading',
  'own voices reader': 'Sensitivity Reading', 'ownvoices reader': 'Sensitivity Reading',
  'pr': 'PR / Marketing', 'pr/marketing': 'PR / Marketing', 'pr & marketing': 'PR / Marketing',
  'public relations': 'PR / Marketing', 'marketing': 'PR / Marketing',
  'book marketing': 'PR / Marketing', 'author marketing': 'PR / Marketing', 'book publicity': 'PR / Marketing',
  'social media': 'Social Media Manager', 'social media consulting': 'Social Media Manager',
  'social media management': 'Social Media Manager', 'sm manager': 'Social Media Manager', 'smm': 'Social Media Manager',
  'cover design': 'Book Cover Design', 'cover designer': 'Book Cover Design', 'book design': 'Book Cover Design',
  'book cover': 'Book Cover Design', 'cover art': 'Book Cover Design', 'cover artist': 'Book Cover Design',
  'pa': 'Author PA', 'author assistant': 'Author PA', 'personal assistant': 'Author PA',
  'virtual assistant': 'Author PA', 'va': 'Author PA', 'author va': 'Author PA',

  // Added after reading what readers had actually typed. Without these the same
  // phrasings would go straight back into the review queue on the next save.
  'book-cover': 'Book Cover Design',
  'proofreading/copyediting': 'Editing',
  'arc reading': 'ARC Reading', 'arc reader': 'ARC Reading', 'arc reads': 'ARC Reading',
  'reel creation': 'Reel Creation', 'reels creation': 'Reel Creation', 'reels': 'Reel Creation',

  // French. One reader filled their whole profile in French and was invisible
  // to every filter because of it.
  'création de couverture': 'Book Cover Design', 'creation de couverture': 'Book Cover Design',
  'édition': 'Editing', 'edition': 'Editing',
  'lecture sensible': 'Sensitivity Reading',
  'mise en pages': 'Formatting', 'mise en page': 'Formatting',
};

// Deliberately NOT in the table above: 'alpha/beta reading'. It is two services
// in one line, and normalizeServiceName returns a single name, so it cannot be
// expressed here. The migration splits the existing ones into two entries at the
// same rate; a new one still lands in the admin queue for a person to split.

// Fetches admin-added canonical services from the DB and merges with hardcoded list.
// Called at save time in AskMeAboutBlock and on load in useAuthorDirectory.
export async function fetchCanonicalServices() {
  try {
    const { data, error } = await supabase
      .from('canonical_services')
      .select('name')
      .order('name');
    if (error) return CANONICAL_SERVICES; // fallback to hardcoded on error
    const dbNames = (data || []).map(r => r.name);
    // Merge — hardcoded first, then any DB additions not already in the list
    // Case-insensitive so an admin typing "editing" into the canonical box does
    // not produce a second checkbox alongside the built-in "Editing".
    return [...CANONICAL_SERVICES, ...dbNames.filter(n => !CANONICAL_SERVICES.some(c => sameServiceName(c, n)))];
  } catch {
    return CANONICAL_SERVICES;
  }
}

// A reader types their own service name, so "Cosplayer", "cosplayer" and
// "Cosplayer " are one service with three sets of keystrokes. Every comparison
// between a stored service name and anything else goes through here, so a
// capital letter can never split one service into two queue rows, two filter
// checkboxes, or two mapping jobs.
export function sameServiceName(a, b) {
  return typeof a === 'string' && typeof b === 'string'
    && a.trim().toLowerCase() === b.trim().toLowerCase();
}

// Tries to turn whatever a reader typed into one of the canonical names.
// Returns null if it doesn't match — admin queue picks that up.
// Pass the merged canonical list from fetchCanonicalServices() if available.
export function normalizeServiceName(raw, canonicalList = CANONICAL_SERVICES) {
  if (!raw?.trim()) return null;
  const lower = raw.trim().toLowerCase();
  if (SERVICE_ALIASES[lower]) return SERVICE_ALIASES[lower];
  const exact = canonicalList.find(s => s.toLowerCase() === lower);
  return exact || null;
}

// ── Shared by BOTH the sidebar badge count and the Services tab list ──────
// Using the exact same two functions in both places means the number in
// the sidebar and the number of rows in the tab can never disagree.
import { supabase } from './supabase';

const PAGE_SIZE = 1000; // Supabase caps a single select at 1000 rows

// Fetches every user_profiles row that has an ask_me_about block,
// paging through in batches of 1000 so nothing gets silently cut off
// once the platform has more than 1000 profiles with services listed.
export async function fetchAllProfilesWithServices() {
  let all = [];
  let from = 0;
  while (true) {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('user_id, username, display_name, ask_me_about')
      .not('ask_me_about', 'is', null)
      .range(from, from + PAGE_SIZE - 1);
    if (error) { console.error('fetchAllProfilesWithServices:', error.message); break; }
    all = all.concat(data || []);
    if (!data || data.length < PAGE_SIZE) break; // last page was partial, we're done
    from += PAGE_SIZE;
  }
  return all;
}

// Turns a list of profiles into the flat list of "needs review" rows the
// admin queue displays — one row per service that needs attention.
export function buildServiceReviewRows(profiles) {
  const rows = [];
  profiles.forEach(p => {
    (p.ask_me_about?.services || []).forEach(s => {
      if (!s.needs_review) return;

      const unmatched   = !s.normalized_name && !CANONICAL_SERVICES.some(c => sameServiceName(c, s.name));
      const badCurrency = !!s.currency && s.currency !== '$';

      // Previously an either/or ternary, so a service that was BOTH
      // unmatched AND priced in a foreign currency only ever showed
      // "Unmatched service" — the currency problem was hidden. Now both
      // reasons show if both apply.
      const reasons = [];
      if (unmatched)   reasons.push('Unmatched service');
      if (badCurrency) reasons.push('Non-USD rate');
      if (reasons.length === 0) reasons.push('Unparseable rate');

      rows.push({
        user_id:      p.user_id,
        username:     p.username,
        display_name: p.display_name,
        service:      s.name,
        rate:         s.rate || null,
        unit:         s.unit || null,
        currency:     s.currency || '$',
        normalized:   s.normalized_name || null,
        reason:       reasons.join(' + '),
      });
    });
  });
  return rows;
}